import { toast } from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import type {
  CreateWorkNoteRequest,
  ListWorkNotesResponse,
  WorkNoteItem,
} from "../types";

export const useSendWorkNote = ({
  patientId,
}: {
  patientId: string;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.WorkNotes.SEND(
        patientId,
        0,
      ),
      mutationFn: (workNoteId: number) =>
        axiosInstance.post(`/clinic/work-notes/${workNoteId}/send`),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.WorkNotes.DEFAULT(patientId),
        });
        toast.success("Work note sent successfully.");
      },
      onError: (error: ApiError) => {
        const message = `Failed to send work note: ${error.response?.data?.detail || "unknown error"}. Please try again later.`;
        toast.error(message);
      },
    },
  });
};

export const useWorkNotes = (patientId: string) => {
  return useApi<{ data: ListWorkNotesResponse }, WorkNoteItem[]>({
    queryOptions: {
      queryKey: QueryKeys.Patient.MedicalRecords.WorkNotes.DEFAULT(patientId),
      queryFn: () =>
        axiosInstance.get("/clinic/work-notes/", {
          params: { patientId },
        }),
      select: (response) => response.data.workNotes,
      enabled: Boolean(patientId),
    },
  });
};

export const useCreateWorkNote = ({
  patientId,
  onSuccess,
}: {
  patientId: string;
  onSuccess?: (workNoteId: number) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.WorkNotes.CREATE(patientId),
      mutationFn: (data: CreateWorkNoteRequest) =>
        axiosInstance.post<WorkNoteItem>("/clinic/work-notes/", data),
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.WorkNotes.DEFAULT(patientId),
        });
        onSuccess?.(response.data.workNoteId);
        toast.success("Work note created.");
      },
      onError: (error: ApiError) => {
        const message = `Failed to create work note: ${error.response?.data?.detail || "unknown error"}. Please try again later.`;
        toast.error(message);
      },
    },
  });
};
